import { useMemo } from 'react';
import { useActiveProject } from '../../../../hooks';
import { apiHooks } from '../../../../hooks/useRestAPIHooks';
import {
  SYSTEM_ACTION_PLAN,
  SYSTEM_LIFEOMIC_DATASET,
} from '../../../../util/fhir';

export const CODE_COMPOSITE_REPORT = 'composite-report';

type CompositeReportCarePlan = fhir3.CarePlan & {
  meta?: fhir3.Meta;
};

const isCompositeReportCarePlan = (plan: CompositeReportCarePlan): boolean =>
  plan.meta?.tag?.some(
    tag =>
      tag.system === SYSTEM_ACTION_PLAN && tag.code === CODE_COMPOSITE_REPORT,
  ) ?? false;

const isRelevantStatus = (plan: CompositeReportCarePlan): boolean =>
  plan.status === 'active' || plan.status === 'completed';

const sortByRecency = (
  a: CompositeReportCarePlan,
  b: CompositeReportCarePlan,
): number => {
  // Active plans take precedence over completed
  if (a.status === 'active' && b.status !== 'active') return -1;
  if (b.status === 'active' && a.status !== 'active') return 1;
  // Within the same status, sort by period.end descending (most recent first)
  const aEnd = a.period?.end ?? a.period?.start ?? '';
  const bEnd = b.period?.end ?? b.period?.start ?? '';
  return bEnd.localeCompare(aEnd);
};

/**
 * Fetches FHIR CarePlans for a subject and returns only those tagged as
 * `composite-report`, sorted so the most recent active/completed plan comes
 * first.
 *
 * Safety guarantee: this hook NEVER surfaces standard (AR) CarePlans because
 * it (a) server-side filters by the composite-report type tag and (b) applies
 * a client-side guard on `meta.tag`.
 */
export const useCompositeReportCarePlans = (subjectId: string) => {
  const [project] = useActiveProject();

  const projectDatasetTag = `${SYSTEM_LIFEOMIC_DATASET}|${project.id}`;
  const compositeReportTypeTag = `${SYSTEM_ACTION_PLAN}|${CODE_COMPOSITE_REPORT}`;

  const query = apiHooks.useAPIQuery('GET /v1/fhir/dstu3/CarePlan', {
    patient: subjectId,
    _tag: [projectDatasetTag, compositeReportTypeTag],
  });

  const carePlans = useMemo((): CompositeReportCarePlan[] => {
    const entries = query.data?.entry ?? [];
    return (entries as Array<{ resource?: CompositeReportCarePlan }>)
      .map(entry => entry.resource)
      .filter((r): r is CompositeReportCarePlan => r != null)
      .filter(isCompositeReportCarePlan)
      .filter(isRelevantStatus)
      .sort(sortByRecency);
  }, [query.data]);

  const latestCarePlan = carePlans[0] as CompositeReportCarePlan | undefined;

  return {
    carePlans,
    latestCarePlan,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
